import { useUser } from '@hooks'
import { useState, useCallback } from 'react'
import { useParams } from '@reach/router'
import { isEmpty, without, xor } from 'lodash'
import debounce from 'lodash/fp/debounce'
import { IPortfolioRegion } from '@api'
import useFavoritesTreeContents from './useFavoritesTreeContents'
import useLocationTreeContents from './useLocationTreeContents'
import {
  mapLocationTreeContents,
  filtrerRegionsByString,
  LocationTreeNodeT,
  LocationNodeType,
  renderFavoritesTreeContents,
  renderLocationTreeContents,
} from './utils'

const useTreeContents = (regions: IPortfolioRegion[]) => {
  const { user, updateUserSettings } = useUser()
  const { floorId: paramsFloorId } = useParams()

  const [searchQuery, setSearchQuery] = useState('')

  const {
    locationTreeNodes,
    setLocationContents,
    setLocationContentsForEachNode,
    setLocationTreeNodes,
  } = useLocationTreeContents(regions)
  const {
    favoriteTreeNodes,
    collapseFavorites,
    setFavoriteContents,
    setFavoriteContentsForEachNode,
    updateFavoriteTreeByFavoriteFloors,
  } = useFavoritesTreeContents(regions)

  const userSettings = user?.context.settings
  const favoriteFloors = userSettings?.favoriteFloors || []

  const debouncedSetLocationTreeNodes = makeDebounce((treeNodes: LocationTreeNodeT[]) => {
    setLocationTreeNodes(treeNodes)
  })

  const updateLocationTreeBySearch = useCallback(
    (searchQuery: string) => {
      const filteredRegions = filtrerRegionsByString(regions, searchQuery)
      const treeNodes = mapLocationTreeContents(
        isEmpty(searchQuery) ? regions : filteredRegions,
        paramsFloorId,
        !isEmpty(searchQuery)
      )

      debouncedSetLocationTreeNodes(treeNodes)
    },
    [debouncedSetLocationTreeNodes, regions, paramsFloorId]
  )

  // handlers
  const handleLocationNodeClick = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    const clickedNodeType = treeNode?.nodeData?.nodeType

    const forEachCallback = (node: LocationTreeNodeT) => {
      const currentNodeType = node?.nodeData?.nodeType

      if (
        clickedNodeType === LocationNodeType.Floor &&
        currentNodeType === LocationNodeType.Floor
      ) {
        node.isSelected = node.id === treeNode.id
      }
    }

    setLocationContents(nodePath, (node) => {
      if (clickedNodeType !== LocationNodeType.Location) {
        node.isExpanded = !treeNode.isExpanded
      }
    })

    setLocationContentsForEachNode(forEachCallback)
    setFavoriteContentsForEachNode(forEachCallback)
  }

  const handleLocationNodeExpand = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    setLocationContents(nodePath, (node) => (node.isExpanded = true))
  }

  const handleLocationNodeCollapse = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    setLocationContents(nodePath, (node) => (node.isExpanded = false))
  }

  const handleLocationToggleFavorite = (id: string, isDefault: boolean) => {
    const updatedFavoriteFloors = xor(favoriteFloors, [id])

    updateFavoriteTreeByFavoriteFloors(updatedFavoriteFloors)
    updateUserSettings(
      isDefault
        ? {
            favoriteFloors: updatedFavoriteFloors,
            defaultFloor: null,
          }
        : {
            favoriteFloors: updatedFavoriteFloors,
          }
    )
  }

  const handleFavoritesNodeClick = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    const clickedNodeType = treeNode?.nodeData?.nodeType

    const forEachCallback = (node: LocationTreeNodeT) => {
      const currentNodeType = node?.nodeData?.nodeType

      if (
        clickedNodeType === LocationNodeType.Floor &&
        currentNodeType === LocationNodeType.Floor
      ) {
        node.isSelected = node.id === treeNode.id
      }
    }

    setFavoriteContents(nodePath, (node) => {
      if (clickedNodeType === LocationNodeType.Location) {
        node.isExpanded = !treeNode.isExpanded
        node.isExpanded = !treeNode.isExpanded
      }
    })

    setFavoriteContentsForEachNode(forEachCallback)
    setLocationContentsForEachNode(forEachCallback)
  }

  const handleFavoritesNodeCollapse = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    setFavoriteContents(nodePath, (node) => (node.isExpanded = false))
  }

  const handleFavoritesNodeExpand = (treeNode: LocationTreeNodeT, nodePath: number[]) => {
    setFavoriteContents(nodePath, (node) => (node.isExpanded = true))
  }

  const handleToggleDefaultLocation = (defaultFloor: string | null) => {
    updateUserSettings({ defaultFloor })
  }

  const handleRemoveFavorite = (id: string, isDefault: boolean) => {
    const updatedFavoriteFloors = without(favoriteFloors, id)

    updateFavoriteTreeByFavoriteFloors(updatedFavoriteFloors)
    updateUserSettings(
      isDefault
        ? {
            favoriteFloors: updatedFavoriteFloors,
            defaultFloor: null,
          }
        : {
            favoriteFloors: updatedFavoriteFloors,
          }
    )
  }

  const handleSearch = (query: string) => {
    collapseFavorites()
    setSearchQuery(query)
    updateLocationTreeBySearch(query)
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    updateLocationTreeBySearch('')
  }

  return {
    searchQuery,
    handleClearSearch,
    handleSearch,
    handleLocationNodeClick,
    handleLocationNodeExpand,
    handleLocationNodeCollapse,
    handleFavoritesNodeClick,
    handleFavoritesNodeCollapse,
    handleFavoritesNodeExpand,
    treeLocationContents: renderLocationTreeContents(locationTreeNodes, {
      userSettings,
      handleToggleFavorite: handleLocationToggleFavorite,
    }),
    favoriteTreeContents: renderFavoritesTreeContents(favoriteTreeNodes, {
      userSettings,
      handleRemoveFavorite,
      handleToggleDefaultLocation,
    }),
  }
}

export default useTreeContents

const makeDebounce = debounce(200)
